import { useEffect, useState, useRef } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import api from '../api/client'

const ROLE_LABELS: Record<string, string> = {
  company_admin: 'Administrador', warehouse_manager: 'Jefe Almacén', operator: 'Operario', readonly: 'Solo Lectura'
}

export default function TenantDetail () {
  const { id } = useParams<{ id: string }>()
  const navigate = useNavigate()
  const [tenant, setTenant] = useState<any>(null)
  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState(false)
  const [uploadingLogo, setUploadingLogo] = useState(false)
  const [logoPreview, setLogoPreview] = useState<string | null>(null)
  const [form, setForm] = useState({ status: '', plan: '' })
  const fileInputRef = useRef<HTMLInputElement>(null)

  function load () {
    api.get(`/tenants/${id}`).then(res => {
      const t = res.data.data
      setTenant(t)
      setForm({ status: t.status, plan: t.plan })
      setLogoPreview(t.logo_url || null)
    }).finally(() => setLoading(false))
  }
  useEffect(load, [id])

  async function handleSave () {
    setSaving(true)
    await api.put(`/tenants/${id}`, form)
    setSaving(false)
    load()
  }

  async function handleLogoUpload (e: React.ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0]
    if (!file) return

    const ext = file.name.split('.').pop()?.toLowerCase() || 'jpg'
    if (!['jpg', 'jpeg', 'png', 'webp', 'svg'].includes(ext)) {
      alert('Formato no soportado. Usa JPG, PNG, WEBP o SVG.')
      return
    }

    setUploadingLogo(true)
    try {
      // 1. Obtener presigned URL (el admin actúa sobre el tenant seleccionado)
      const { data } = await api.get(`/upload/logo-url?ext=${ext}&tenantId=${id}`)
      const { uploadUrl, logoUrl } = data.data

      // 2. Subir directamente a S3
      await fetch(uploadUrl, {
        method: 'PUT',
        body: file,
        headers: { 'Content-Type': file.type }
      })

      // 3. Guardar logo_url en el tenant
      await api.put(`/tenants/${id}`, { logoUrl })

      setLogoPreview(logoUrl + '?t=' + Date.now()) // forzar recarga
      setTenant((prev: any) => ({ ...prev, logo_url: logoUrl }))
    } catch (err: any) {
      alert('Error al subir el logo: ' + (err.message || 'intenta nuevamente'))
    } finally {
      setUploadingLogo(false)
      if (fileInputRef.current) fileInputRef.current.value = ''
    }
  }

  async function handleRemoveLogo () {
    if (!window.confirm('¿Quitar el logo de esta empresa?')) return
    await api.put(`/tenants/${id}`, { logoUrl: null })
    setLogoPreview(null)
    setTenant((prev: any) => ({ ...prev, logo_url: null }))
  }

  if (loading) return <div className="loading">Cargando...</div>
  if (!tenant) return <div className="alert alert-error">Empresa no encontrada</div>

  return (
    <>
      <div className="page-header">
        <div>
          <div className="page-title">{tenant.name}</div>
          <div className="page-subtitle">{tenant.email} · {tenant.slug}</div>
        </div>
        <button className="btn btn-secondary" onClick={() => navigate('/empresas')}>← Volver</button>
      </div>

      <div className="page-body">
        <div className="grid-2" style={{ gap: 20, alignItems: 'start' }}>
          <div>
            {/* Logo de la empresa */}
            <div className="card mb-4">
              <div className="card-header"><h3 className="card-title">Logo de la empresa</h3></div>
              <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 16, padding: '8px 0' }}>
                <div style={{
                  width: 200, height: 100, border: '2px dashed var(--border)',
                  borderRadius: 8, display: 'flex', alignItems: 'center',
                  justifyContent: 'center', background: 'var(--bg)', overflow: 'hidden'
                }}>
                  {logoPreview
                    ? <img src={logoPreview} alt="Logo" style={{ maxWidth: '100%', maxHeight: '100%', objectFit: 'contain' }} />
                    : <span style={{ color: 'var(--text-light)', fontSize: 13 }}>Sin logo</span>
                  }
                </div>

                <div style={{ display: 'flex', gap: 8 }}>
                  <button
                    className="btn btn-primary"
                    onClick={() => fileInputRef.current?.click()}
                    disabled={uploadingLogo}
                  >
                    {uploadingLogo ? 'Subiendo...' : logoPreview ? 'Cambiar logo' : 'Subir logo'}
                  </button>
                  {logoPreview && (
                    <button className="btn btn-secondary" onClick={handleRemoveLogo} disabled={uploadingLogo}>
                      Quitar
                    </button>
                  )}
                </div>

                <input
                  ref={fileInputRef}
                  type="file"
                  accept="image/jpeg,image/png,image/webp,image/svg+xml"
                  style={{ display: 'none' }}
                  onChange={handleLogoUpload}
                />
                <p style={{ fontSize: 12, color: 'var(--text-light)', textAlign: 'center' }}>
                  JPG, PNG, WEBP o SVG · Recomendado: fondo transparente (PNG/SVG)
                </p>
              </div>
            </div>

            {/* Configuración */}
            <div className="card mb-4">
              <div className="card-header"><h3 className="card-title">Configuración</h3></div>
              <div className="form-group">
                <label className="form-label">Estado</label>
                <select className="form-control" value={form.status} onChange={e => setForm(p => ({ ...p, status: e.target.value }))}>
                  <option value="active">Activo</option>
                  <option value="inactive">Inactivo</option>
                  <option value="trial">Trial</option>
                </select>
              </div>
              <div className="form-group">
                <label className="form-label">Plan</label>
                <select className="form-control" value={form.plan} onChange={e => setForm(p => ({ ...p, plan: e.target.value }))}>
                  <option value="standard">Standard</option>
                  <option value="premium">Premium</option>
                </select>
              </div>
              <button className="btn btn-primary" onClick={handleSave} disabled={saving}>
                {saving ? 'Guardando...' : 'Guardar cambios'}
              </button>
            </div>

            {/* Información */}
            <div className="card">
              <div className="card-header"><h3 className="card-title">Información</h3></div>
              <div className="text-sm">
                <p><strong>Tax ID:</strong> {tenant.tax_id || '—'}</p>
                <p className="mt-4"><strong>Teléfono:</strong> {tenant.phone || '—'}</p>
                <p className="mt-4"><strong>Dirección:</strong> {tenant.address || '—'}</p>
                <p className="mt-4"><strong>Creado:</strong> {new Date(tenant.created_at).toLocaleDateString('es-CL')}</p>
              </div>
            </div>
          </div>

          {/* Usuarios */}
          <div className="card">
            <div className="card-header"><h3 className="card-title">Usuarios ({tenant.users?.length || 0})</h3></div>
            <table>
              <thead><tr><th>Nombre</th><th>Email</th><th>Rol</th><th>Estado</th></tr></thead>
              <tbody>
                {tenant.users?.map((u: any) => (
                  <tr key={u.id}>
                    <td>{u.first_name} {u.last_name}</td>
                    <td>{u.email}</td>
                    <td>{ROLE_LABELS[u.role] || u.role}</td>
                    <td><span className={`badge ${u.is_active ? 'badge-success' : 'badge-neutral'}`}>{u.is_active ? 'Activo' : 'Inactivo'}</span></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </>
  )
}
